import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@lsq/nextgen/v2/button';
// import { SortOutlined } from "../LsqIcon";
import Tooltip from '@lsq/nextgen/tooltip';
import { Theme } from '@lsq/nextgen/common/common.types';
import { Placement, Trigger } from '@lsq/nextgen/tooltip/tooltip.types';
import { useFeature } from '../../../../hooks/useFeature';
import { useFeatureAnalytics } from '../../../../hooks/useFeatureAnalytics';

/**
 * Sort toggle for listing pages. Toggles the active sort column between the
 * two configured `sortItems` (e.g. "Last Modified" <-> "Created On").
 *
 * Accepts sortState directly from useListingState.
 * Returns null if sortState or sortItems are not provided.
 */
const SortFilter = ({ sortState, sortItems = [] }) => {
  const [sortConfig, setSortConfig] = sortState || [];
  const feature = useFeature();
  const config = feature?.config;
  const track = useFeatureAnalytics(config);

  const handleToggleSort = useCallback(() => {
    setSortConfig((prev) => {
      const nextIndex = sortItems[0].id === prev.sortColumn ? 1 : 0;
      const nextSort = sortItems[nextIndex];

      // Track analytics when sort changes
      if (config?.analytics?.filterChange) {
        track('filterChange', { value: nextSort.label });
      }

      return {
        ...prev,
        label: nextSort.label,
        sortColumn: nextSort.id
      };
    });
  }, [setSortConfig, sortItems, config, track]);

  // Needs both the toggle target list and a place to write the result.
  if (!sortState || sortItems.length < 2) return null;

  // Title-case only the displayed tooltip text (e.g. "recently modified" -> "Recently
  // Modified"). The underlying sortConfig.label stays as-is so sort logic is unaffected.
  const tooltipContent = (sortConfig.label || '').replace(/\b\w/g, (c) =>
    c.toUpperCase()
  );

  return (
    <Tooltip
      content={tooltipContent}
      theme={Theme.Dark}
      placement={Placement.Vertical}
      trigger={[Trigger.Hover]}>
      <IconButton
        onClick={handleToggleSort}
        size="sm"
        icon={<img src="/images/launcherIcons/lsq_sort.svg" alt="sort" />}
      />
    </Tooltip>
  );
};

SortFilter.propTypes = {
  sortState: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        sortColumn: PropTypes.string,
        sortOrder: PropTypes.number
      }),
      PropTypes.func
    ])
  ),
  sortItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string
    })
  )
};

export default SortFilter;
